import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { IssueReportSource } from '../../redux/features/issueReport/issueReport.props';
import {
  selectIsIssueReportingEnabled,
  selectIssueReportingVariant,
  selectIssueTypes,
} from '../../redux/features/config/config.selector';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectActiveRankedCandidates,
  selectIssueReportTotalCount,
  selectIssueReports,
  selectStagedIssueType,
  selectStagedRank,
  selectUnclassifiedIssueReportCount,
} from '../../redux/features/issueReport/issueReport.selectors';
import {
  clearStagedIssueType,
  closeFlagWindow,
  commitIssueType,
  flagIssue,
  IssueRanker,
  stageIssueType,
  voidIssue,
} from '../../redux/features/issueReport/issueReport.slice';

/**
 * HITL-1492 Pillar 3, Part A — the single seam HITL-1499/1500/1501 consume.
 *
 * Unused in this ticket by design. It exists here so those three tickets can be built
 * in parallel against one agreed shape rather than each inventing its own dispatch
 * calls and drifting.
 *
 * The flag gate lives here, ONCE, so no caller can leak a network call with the
 * feature off.
 *
 * @param rank HITL-1500's `rankIssueTypes`. Omitted by HITL-1499, so
 * `ranked_candidates` stays `[]` until 1500 changes this one call site. Injected
 * rather than imported so 1498/1499 do not depend on a module that does not exist yet,
 * and so the ranker still runs exactly once, at flag time.
 */
const useIssueReport = ({ rank }: { rank?: IssueRanker } = {}) => {
  const dispatch = useAppDispatch();

  const isEnabled = useSelector(selectIsIssueReportingEnabled);
  const issueTypes = useSelector(selectIssueTypes);
  const variant = useSelector(selectIssueReportingVariant);

  const reports = useSelector(selectIssueReports);
  const activeReport = useSelector(selectActiveIssueReport);
  const activeWindowExpiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const rankedCandidates = useSelector(selectActiveRankedCandidates);
  const stagedType = useSelector(selectStagedIssueType);
  const stagedRank = useSelector(selectStagedRank);
  const unclassifiedCount = useSelector(selectUnclassifiedIssueReportCount);
  const totalCount = useSelector(selectIssueReportTotalCount);

  /** Returns the new `report_id`, so HITL-1499's toast can address its own record. */
  const flag = useCallback(
    (
      args: {
        source?: IssueReportSource;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      if (!isEnabled) return null;
      return dispatch(flagIssue({ ...args, rank }));
    },
    [isEnabled, rank, dispatch]
  );

  const commit = useCallback(
    (args: {
      report_id: string;
      issue_type: string | null;
      chosen_rank?: number | null;
      details?: string | null;
    }) => {
      if (!isEnabled) return null;
      return dispatch(commitIssueType(args));
    },
    [isEnabled, dispatch]
  );

  const voidReport = useCallback(
    (reportId: string) => {
      if (!isEnabled) return null;
      return dispatch(voidIssue({ report_id: reportId }));
    },
    [isEnabled, dispatch]
  );

  const stage = useCallback(
    (issueType: string, rankIndex: number | null) => {
      if (!isEnabled) return;
      dispatch(stageIssueType({ issue_type: issueType, rank: rankIndex }));
    },
    [isEnabled, dispatch]
  );

  const clearStaged = useCallback(() => {
    if (!isEnabled) return;
    dispatch(clearStagedIssueType());
  }, [isEnabled, dispatch]);

  const closeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(closeFlagWindow());
  }, [isEnabled, dispatch]);

  return {
    isEnabled,
    issueTypes,
    variant,
    reports,
    activeReport,
    activeWindowExpiresAt,
    rankedCandidates,
    stagedType,
    stagedRank,
    unclassifiedCount,
    totalCount,
    flagIssue: flag,
    commitIssueType: commit,
    voidIssue: voidReport,
    stageIssueType: stage,
    clearStagedIssueType: clearStaged,
    closeFlagWindow: closeWindow,
  };
};

export default useIssueReport;
